import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shuffle, Users, Calendar } from "lucide-react";
import Button from "../forms/Button";
import { axiosPrivate } from "../../api/axios";
import { formatDateLocal } from "../../utils/dateUtils";

const RandomWFHModal = ({ isOpen, onClose, weekStart, onSuccess }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [daysPerEmployee, setDaysPerEmployee] = useState(2);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [selectedWeekOption, setSelectedWeekOption] = useState("current"); // "current" or "next"

  // Fetch all employees when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen]);

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await axiosPrivate.get("/api/employees");

      console.log("Full response:", response);
      console.log("Response data:", response.data);
      console.log("Response data type:", typeof response.data);

      // Handle different response structures
      let employeesData = [];

      if (Array.isArray(response.data)) {
        employeesData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        employeesData = response.data.data;
      } else if (
        response.data?.employees &&
        Array.isArray(response.data.employees)
      ) {
        employeesData = response.data.employees;
      }

      console.log("Employees data:", employeesData);
      console.log("Total employees:", employeesData.length);

      // Don't filter by status - show all employees
      setEmployees(employeesData);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      console.error("Error response:", error.response);
      console.error("Error details:", error.response?.data || error.message);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleToggleEmployee = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId],
    );
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map((emp) => emp._id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedEmployees.length === 0) {
      return;
    }

    setLoading(true);

    try {
      // Calculate the target week based on selection
      let targetWeekStart = new Date(weekStart);
      if (selectedWeekOption === "next") {
        targetWeekStart.setDate(targetWeekStart.getDate() + 7);
      }

      await onSuccess({
        weekStart: formatDateLocal(targetWeekStart),
        employeeIds: selectedEmployees,
        daysPerEmployee: Number(daysPerEmployee),
      });

      // Reset and close
      setSelectedEmployees([]);
      setDaysPerEmployee(2);
      setSelectedWeekOption("current");
      onClose();
    } catch (error) {
      // Error is handled by parent
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-primary-card rounded-2xl shadow-2xl w-full max-w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-primary-accent to-orange-600 rounded-lg">
                <Shuffle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-light-100">
                  Random WFH Generator
                </h2>
                <p className="text-sm text-gray-500 dark:text-light-500">
                  Automatically create WFH requests for selected employees
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-dark-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-light-400" />
            </button>
          </div>

          {/* Content */}
          <form
            onSubmit={handleSubmit}
            className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]"
          >
            {/* Week Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-light-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Select Week
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedWeekOption("current")}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    selectedWeekOption === "current"
                      ? "border-primary-accent bg-primary-accent/10 text-primary-accent font-semibold"
                      : "border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-700 dark:text-light-300 hover:border-primary-accent/50"
                  }`}
                >
                  <div className="text-sm font-medium">Current Week</div>
                  <div className="text-xs mt-1">
                    {weekStart?.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedWeekOption("next")}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    selectedWeekOption === "next"
                      ? "border-primary-accent bg-primary-accent/10 text-primary-accent font-semibold"
                      : "border-gray-300 dark:border-dark-200 bg-white dark:bg-dark-100 text-gray-700 dark:text-light-300 hover:border-primary-accent/50"
                  }`}
                >
                  <div className="text-sm font-medium">Next Week</div>
                  <div className="text-xs mt-1">
                    {(() => {
                      const nextWeek = new Date(weekStart);
                      nextWeek.setDate(nextWeek.getDate() + 7);
                      return nextWeek.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      });
                    })()}
                  </div>
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-light-500 mt-2">
                WFH days will be randomly assigned to weekdays (Sun-Thu)
              </p>
            </div>

            {/* Days Per Employee */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-light-300 mb-2">
                Days per Employee
              </label>
              <select
                value={daysPerEmployee}
                onChange={(e) => setDaysPerEmployee(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-accent focus:border-transparent"
              >
                <option value="1">1 day</option>
                <option value="2">2 days</option>
                <option value="3">3 days</option>
                <option value="4">4 days</option>
                <option value="5">5 days</option>
              </select>
            </div>

            {/* Employee Selection */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-light-300">
                  <Users className="w-4 h-4 inline mr-2" />
                  Select Employees ({selectedEmployees.length} selected)
                </label>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm font-medium text-primary-accent hover:text-orange-600"
                >
                  {selectedEmployees.length === employees.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              {loadingEmployees ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-accent mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-light-500 mt-2">
                    Loading employees...
                  </p>
                </div>
              ) : employees.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-light-500">
                  No employees found
                </div>
              ) : (
                <div className="border border-gray-200 dark:border-dark-200 rounded-lg max-h-80 overflow-y-auto">
                  {employees.map((employee) => (
                    <label
                      key={employee._id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-dark-400 cursor-pointer border-b border-gray-100 dark:border-dark-100 last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(employee._id)}
                        onChange={() => handleToggleEmployee(employee._id)}
                        className="w-4 h-4 text-primary-accent focus:ring-primary-accent rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                          {employee.fullNameEn}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-light-500 truncate">
                          {employee.employeeCode} • {employee.team}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {selectedEmployees.length === 0 && !loadingEmployees && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                Please select at least one employee
              </p>
            )}
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-dark-100">
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                loading || selectedEmployees.length === 0 || loadingEmployees
              }
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Shuffle className="w-4 h-4 mr-2" />
                  Generate WFH Requests
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default RandomWFHModal;
